/**
 * PlatformPublishCard — section "Publication X" sur WorkDetailPage
 * (S6 PR2-D quater — successeur de `EtsyPublishCard`).
 *
 * Composant générique paramétré par `platform: Platform`. En S6 seule
 * `platform="etsy"` est branchée côté BE — Pinterest/Instagram/TikTok
 * lèveront 422 "pas encore implémentée" au POST. Pour S7+ il suffira
 * de passer un autre `platform` sans modifier ce composant.
 *
 * Pattern S5b queue + scheduler (pas de publish HTTP synchrone côté FE).
 *
 * Affiche :
 *   - Publications déjà postées pour cette œuvre + plateforme (avec lien
 *     externe vers la page plateforme et badge état).
 *   - Bouton "Publier sur X" qui :
 *       1. Confirme via useConfirm
 *       2. POST /api/works/{id}/publications/{platform} → enqueue (202 Accepted)
 *       3. Toast info "Publication mise en file"
 *       4. Active un polling 5s sur useWorkPublications jusqu'à
 *          apparition de la nouvelle row OU timeout 90s.
 *
 * Limitations volontaires (replacé par `<PublishWizard>` complet en PR4) :
 *   - 1 publication à la fois (pas de batch)
 *   - Sélection variant + draft auto (pas de UI de choix)
 *   - Pas de preview avant POST
 *
 * Verrous tenus :
 *   - Aucun `<button>` natif (utilise `<Button>`)
 *   - Aucun `window.confirm` (utilise `useConfirm`)
 *   - Toasts en info passive uniquement
 */

import { useState } from 'react'
import { ExternalLink, Loader2, Send, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import MockupTemplatePicker from '@/components/etsy/MockupTemplatePicker'
import EndPublicationModal from '@/components/work/EndPublicationModal'
import PublicationUpdateModal from '@/components/work/PublicationUpdateModal'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useDrafts } from '@/hooks/useDrafts'
import { useMatrixForWork } from '@/hooks/useWorkPodPhysical'
import {
  useDeleteWithReconciliation,
  useEndPublication,
  useEnqueuePublication,
  useRenewPublication,
  useUpdatePublication,
  useWorkPublications,
} from '@/hooks/useWorkPublications'
import { workPodPhysicalApi } from '@/utils/api/work-pod-physical'
import {
  ETSY_LISTING_STATE_FR,
  type Platform,
  type PlatformResourceResponse,
} from '@/types/publications'
import type { WorkVariantWithCatalogResponse } from '@/types/works'

import './PlatformPublishCard.css'

interface PlatformPublishCardProps {
  workId: number
  workTitle: string
  variants: WorkVariantWithCatalogResponse[]
  platform: Platform
}

// Polling : 5s tant qu'on attend, abandon après 90s (3 ticks scheduler
// par sécurité). Le scheduler tique toutes les 30s — un job nominal
// arrive en <60s ; au-delà, suspicion d'échec persistant.
const POLL_INTERVAL_MS = 5_000
const POLL_TIMEOUT_MS = 90_000

// Labels FR par plateforme (UI). Ajouter ici quand une plateforme arrive
// (Pinterest S7+, etc.). Si plateforme inconnue → fallback Etsy-style.
const PLATFORM_LABEL_FR: Record<Platform, string> = {
  etsy: 'Etsy',
  pinterest: 'Pinterest',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  saatchi: 'Saatchi',
  artfinder: 'Artfinder',
}

// Mapping draft kind par plateforme. Pour Etsy v12 : split en 2 kinds
// (PHYSICAL / DIGITAL) — sélection dynamique par `variant.medium_kind`
// via `expectedDraftKindForVariant` ci-dessous, donc la valeur Etsy
// reste un fallback générique (uniquement utilisé pour la query de drafts
// quand on n'a pas encore choisi de variante).
const PLATFORM_DRAFT_KIND: Record<Platform, string> = {
  etsy: 'etsy_listing_physical',
  pinterest: 'pinterest_pin',
  instagram: 'instagram_post',
  tiktok: 'tiktok_post',
  saatchi: 'saatchi_listing',
  artfinder: 'artfinder_listing',
}

/**
 * Résout le DraftKind attendu pour une (plateforme, variante) — refonte
 * drafts v12. Pour Etsy, le kind dépend de `variant.medium_kind` :
 *   - print | original → etsy_listing_physical
 *   - digital → etsy_listing_digital
 * Pour les autres plateformes (medium-agnostiques), retourne le kind
 * fixe de la plateforme.
 */
function expectedDraftKindForVariant(
  platform: Platform,
  mediumKind: string | null | undefined,
): string {
  if (platform === 'etsy') {
    return mediumKind === 'digital' ? 'etsy_listing_digital' : 'etsy_listing_physical'
  }
  return PLATFORM_DRAFT_KIND[platform] ?? `${platform}_listing`
}

export default function PlatformPublishCard({
  workId,
  workTitle,
  variants,
  platform,
}: PlatformPublishCardProps) {
  const draftsQuery = useDrafts(workId)
  const enqueueMutation = useEnqueuePublication({ workId, platform })
  // Matrice POD active (Phase 8 — câblage refonte M). Si présente sur
  // Etsy + variante print, on route vers publish_matrix au lieu du
  // single-variant publish.
  const matrixQuery = useMatrixForWork(platform === 'etsy' ? workId : null)
  const activeMatrix = matrixQuery.data ?? null

  const platformLabel = PLATFORM_LABEL_FR[platform] ?? platform
  const expectedDraftKind = PLATFORM_DRAFT_KIND[platform] ?? `${platform}_listing`

  // `pendingSince` : timestamp ms du dernier enqueue. Active un polling
  // 5s sur la liste des publications jusqu'à apparition de la nouvelle
  // row (visible visuellement par l'utilisateur dans la liste).
  const [pendingSince, setPendingSince] = useState<number | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Modales lifecycle PR2-C — l'une des deux (update / end) à la fois.
  const [updateTarget, setUpdateTarget] = useState<{
    resourceId: number
    currentTemplateIds: number[]
    variantFormatSlug?: string | null
  } | null>(null)
  const [endTarget, setEndTarget] = useState<{ resourceId: number } | null>(null)

  const publicationsQuery = useWorkPublications({
    workId,
    platform,
    pollMs: pendingSince !== null ? POLL_INTERVAL_MS : undefined,
  })
  const updateMutation = useUpdatePublication({ workId, platform })
  const endMutation = useEndPublication({ workId, platform })
  const renewMutation = useRenewPublication({ workId, platform })
  const deleteWithReconciliationMutation = useDeleteWithReconciliation({ workId, platform })
  const { confirm } = useConfirm()

  const publications = publicationsQuery.data ?? []
  const drafts = draftsQuery.data ?? []
  // Refonte drafts v12 — pour Etsy, garder TOUS les drafts approuvés
  // (PHYSICAL et DIGITAL). La résolution kind par variant.medium_kind se
  // fait au moment du publish (pas au filtre initial). Pour les autres
  // plateformes (mono-kind), le filtre exact tient.
  const approvedDraftsForPlatform =
    platform === 'etsy'
      ? drafts.filter(
          (d) =>
            (d.kind === 'etsy_listing_physical' || d.kind === 'etsy_listing_digital') &&
            d.status === 'approved',
        )
      : drafts.filter((d) => d.kind === expectedDraftKind && d.status === 'approved')

  // Variantes actives sans publication active sur cette plateforme.
  // `'ended'` (retiré, réversible via renew) et `'deleted'` (supprimé
  // définitivement, soit côté Etsy soit réconcilié 404) sont les deux
  // états terminaux : ils ne bloquent pas la republication.
  const variantsWithActivePublication = new Set(
    publications
      .filter((p) => p.state !== 'ended' && p.state !== 'deleted')
      .map((p) => p.work_variant_id),
  )

  // Les publications terminales `'deleted'` sont conservées pour le
  // journal forensique (cf. <WorkPublicationsTab>) mais polluent la
  // vue "Publication X" qui sert à piloter l'état courant — on les
  // masque ici, l'historique reste consultable dans l'onglet Journal.
  const visiblePublications = publications.filter((p) => p.state !== 'deleted')
  const publishableVariants = variants.filter(
    (v) => v.status === 'active' && !variantsWithActivePublication.has(v.id),
  )

  // Drafts indexés par kind pour résolution rapide (Etsy v12 split).
  const draftsByKind = approvedDraftsForPlatform.reduce<Record<string, (typeof drafts)[number]>>(
    (acc, d) => {
      if (!acc[d.kind]) acc[d.kind] = d
      return acc
    },
    {},
  )

  // Pour le check global "y a-t-il au moins un draft approuvé éligible" :
  // un draft de n'importe quel kind acceptable pour cette plateforme.
  const approvedDraft = approvedDraftsForPlatform[0]

  const handleOpenPicker = () => {
    if (publishableVariants.length === 0) {
      toast.info('Aucune variante active publiable.')
      return
    }
    if (!approvedDraft) {
      toast.error(
        `Aucun draft ${platformLabel} approuvé. Approuve d’abord un draft sur la page Drafts.`,
      )
      return
    }
    setPickerOpen(true)
  }

  const handlePublishWithMockups = async (mockupTemplateIds: number[]) => {
    if (!approvedDraft || publishableVariants.length === 0) {
      toast.error('État inattendu — recharge la page.')
      setPickerOpen(false)
      return
    }
    const variant = publishableVariants[0]

    // Refonte drafts v12 — résolution kind par variant.medium_kind. Sur
    // Etsy, on prend le draft PHYSICAL ou DIGITAL selon la variante choisie.
    const targetKind = expectedDraftKindForVariant(platform, variant.medium_kind)
    const draftToPublish = draftsByKind[targetKind] ?? approvedDraft
    if (draftToPublish.kind !== targetKind) {
      toast.error(
        `Aucun draft ${platformLabel} ${
          targetKind === 'etsy_listing_digital' ? '(digital)' : '(physique)'
        } approuvé pour cette variante (${variant.medium_kind}). Génère / approuve le draft correspondant.`,
      )
      setPickerOpen(false)
      return
    }

    try {
      // Phase 8 — refonte M : si l'œuvre a une matrice POD active ET la
      // variante est print, on route vers publish_matrix (1 listing Etsy
      // multi-variant). Sinon on garde l'ancien flow single-variant
      // (digital, ou print sans matrice — rétro-compat œuvres pré-refonte).
      if (
        platform === 'etsy' &&
        activeMatrix !== null &&
        variant.medium_kind === 'print' &&
        draftToPublish.kind === 'etsy_listing_physical'
      ) {
        await workPodPhysicalApi.publish(activeMatrix.id, {
          draft_id: draftToPublish.id,
          mockup_template_ids: mockupTemplateIds,
        })
      } else {
        await enqueueMutation.mutateAsync({
          variant_id: variant.id,
          draft_id: draftToPublish.id,
          mockup_template_ids: mockupTemplateIds,
        })
      }
      toast.info('Publication mise en file — apparaîtra dans ~30s.')
      setPickerOpen(false)
      setPendingSince(Date.now())
      window.setTimeout(() => {
        setPendingSince((prev) => (prev !== null ? null : prev))
      }, POLL_TIMEOUT_MS)
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Mise en file impossible.'
      toast.error(detail)
    }
  }

  const handleOpenUpdate = (publication: PlatformResourceResponse) => {
    if (approvedDraftsForPlatform.length === 0) {
      toast.error(
        `Aucun draft ${platformLabel} approuvé. Approuve d’abord un draft sur la page Drafts.`,
      )
      return
    }
    setUpdateTarget({
      resourceId: publication.id,
      currentTemplateIds: publication.mockups.map((m) => m.mockup_template_id),
    })
  }

  const handleConfirmUpdate = async (payload: {
    draft_id: number
    mockup_template_ids?: number[] | null
  }) => {
    if (!updateTarget) return
    try {
      await updateMutation.mutateAsync({
        resourceId: updateTarget.resourceId,
        payload: {
          draft_id: payload.draft_id,
          mockup_template_ids: payload.mockup_template_ids ?? null,
        },
      })
      toast.info('Mise à jour mise en file — Etsy synchronisé dans ~30s.')
      setUpdateTarget(null)
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Mise en file de l’update impossible.'
      toast.error(detail)
    }
  }

  const handleConfirmEnd = async (reason: 'user_unpublish' | 'sold_direct') => {
    if (!endTarget) return
    try {
      await endMutation.mutateAsync({
        resourceId: endTarget.resourceId,
        reason,
      })
      toast.info(`Retrait mis en file — ${platformLabel} synchronisé dans ~30s.`)
      setEndTarget(null)
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Mise en file du retrait impossible.'
      toast.error(detail)
    }
  }

  const handleDeleteWithReconciliation = async (publication: PlatformResourceResponse) => {
    const ok = await confirm({
      title: `Supprimer la publication ${platformLabel}`,
      message:
        platform === 'etsy'
          ? "Le listing Etsy sera vérifié puis supprimé définitivement s'il existe encore. Si Stéphanie l'a déjà supprimé manuellement sur etsy.com, sgraphic.art s'aligne automatiquement. La trace dans le journal est conservée. Continuer ?"
          : `La publication ${platformLabel} sera supprimée. La trace dans le journal est conservée. Continuer ?`,
      confirmText: 'Supprimer',
      variant: 'danger',
    })
    if (!ok) return
    try {
      const result = await deleteWithReconciliationMutation.mutateAsync({
        resourceId: publication.id,
      })
      if (result.status === 'reconciled_local_only') {
        toast.info(`Listing déjà absent sur ${platformLabel} — sgraphic.art est aligné.`)
      } else {
        toast.info(`Suppression mise en file — ${platformLabel} synchronisé dans ~30s.`)
      }
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Suppression impossible.'
      toast.error(detail)
    }
  }

  const handleRenew = async (publication: PlatformResourceResponse) => {
    const ok = await confirm({
      title: `Renouveler la publication ${platformLabel}`,
      message:
        platform === 'etsy'
          ? 'Etsy facture 0,20 € pour réinitialiser la fraîcheur SEO du listing (4 mois supplémentaires). Confirmer ?'
          : `Renouveler la fraîcheur de cette publication ${platformLabel} ?`,
      confirmText: 'Renouveler',
    })
    if (!ok) return
    try {
      await renewMutation.mutateAsync({ resourceId: publication.id })
      toast.info('Renouvellement mis en file.')
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Mise en file du renouvellement impossible.'
      toast.error(detail)
    }
  }

  // Publication ciblée par les modales (lookup pour passer les méta au render).
  const updateTargetPublication =
    updateTarget !== null ? publications.find((p) => p.id === updateTarget.resourceId) : undefined
  const endTargetPublication =
    endTarget !== null ? publications.find((p) => p.id === endTarget.resourceId) : undefined

  if (publicationsQuery.isLoading) {
    return (
      <section className="platform-publish-card" aria-label={`Publication ${platformLabel}`}>
        <p className="ui-body ui-text-muted" role="status">
          Chargement des publications {platformLabel}…
        </p>
      </section>
    )
  }

  const isPending = enqueueMutation.isPending || pendingSince !== null

  return (
    <section className="platform-publish-card" aria-label={`Publication ${platformLabel}`}>
      <header className="platform-publish-card-header">
        <div>
          <h3 className="ui-h4 platform-publish-card-title">Publication {platformLabel}</h3>
          <p className="ui-body ui-text-muted platform-publish-card-subtitle">
            {pendingSince !== null
              ? 'Publication en cours — le scheduler tique toutes les 30s…'
              : visiblePublications.length === 0
                ? activeMatrix
                  ? `Cette œuvre n’a pas encore été publiée sur ${platformLabel}. Approuve et publie depuis l’onglet « Contenu généré » (preview des composites avant publish).`
                  : `Cette œuvre n’a pas encore été publiée sur ${platformLabel}.`
                : `${visiblePublications.length} publication${visiblePublications.length > 1 ? 's' : ''} sur ${platformLabel}.`}
          </p>
        </div>
        {publishableVariants.length > 0 && !activeMatrix ? (
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleOpenPicker}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 aria-hidden="true" size={16} className="platform-publish-card-spinner" />
            ) : (
              <Send aria-hidden="true" size={16} />
            )}
            {pendingSince !== null ? 'En file…' : `Publier sur ${platformLabel}`}
          </Button>
        ) : null}
      </header>

      {visiblePublications.length > 0 ? (
        <ul className="platform-publish-card-list">
          {visiblePublications.map((pub) => (
            <PlatformPublicationRow
              key={pub.id}
              publication={pub}
              variants={variants}
              onUpdate={handleOpenUpdate}
              onEnd={(p) => setEndTarget({ resourceId: p.id })}
              onRenew={handleRenew}
              onDeleteSmart={handleDeleteWithReconciliation}
              isLifecycleBusy={
                updateMutation.isPending ||
                endMutation.isPending ||
                renewMutation.isPending ||
                deleteWithReconciliationMutation.isPending
              }
            />
          ))}
        </ul>
      ) : null}

      {publishableVariants.length > 0 ? (
        <MockupTemplatePicker
          workId={workId}
          workTitle={workTitle}
          // Pas de filtrage par format_slug (la variante peut porter N
          // formats via `work_variant_formats`). Les templates retournés
          // sont scorés par tags + palette, suffisant pour le top 10.
          formatSlug={null}
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={handlePublishWithMockups}
          isSubmitting={enqueueMutation.isPending}
        />
      ) : null}

      {updateTarget !== null && updateTargetPublication !== undefined ? (
        <PublicationUpdateModal
          isOpen
          onClose={() => setUpdateTarget(null)}
          workId={workId}
          workTitle={workTitle}
          variantFormatSlug={updateTarget.variantFormatSlug ?? null}
          availableDrafts={approvedDraftsForPlatform}
          currentMockupTemplateIds={updateTarget.currentTemplateIds}
          platformLabel={platformLabel}
          onConfirm={handleConfirmUpdate}
          isSubmitting={updateMutation.isPending}
        />
      ) : null}

      {endTarget !== null && endTargetPublication !== undefined ? (
        <EndPublicationModal
          isOpen
          onClose={() => setEndTarget(null)}
          platformLabel={platformLabel}
          workTitle={workTitle}
          onConfirm={handleConfirmEnd}
          isSubmitting={endMutation.isPending}
        />
      ) : null}
    </section>
  )
}

interface PlatformPublicationRowProps {
  publication: PlatformResourceResponse
  variants: WorkVariantWithCatalogResponse[]
  onUpdate: (publication: PlatformResourceResponse) => void
  onEnd: (publication: PlatformResourceResponse) => void
  onRenew: (publication: PlatformResourceResponse) => void
  onDeleteSmart: (publication: PlatformResourceResponse) => void
  /** Désactive les 4 boutons lifecycle pendant qu'une mutation tourne. */
  isLifecycleBusy: boolean
}

// États qui autorisent les 3 actions lifecycle update/end/renew (cohérent
// avec publications_service côté BE — _VALID_STATES_FOR_*).
const LIFECYCLE_MUTABLE_STATES = new Set(['active', 'sold_out', 'edited'])
const RENEW_ALLOWED_STATES = new Set(['active'])
// "Supprimer" intelligent : visible partout sauf si déjà `deleted` (le BE
// expose les mêmes états dans `_VALID_STATES_FOR_RECONCILE`).
const DELETE_SMART_BLOCKED_STATES = new Set(['deleted'])

/** URL canonique vers la fiche produit côté plateforme. Construite à
 * partir de l'`external_id` (pas de `publication.url` car le champ
 * pointe parfois vers une URL générique de la boutique selon l'adapter).
 */
function publicationExternalUrl(platform: Platform, externalId: string): string | null {
  if (platform === 'etsy') {
    return `https://www.etsy.com/listing/${externalId}`
  }
  return null
}

function PlatformPublicationRow({
  publication,
  variants,
  onUpdate,
  onEnd,
  onRenew,
  onDeleteSmart,
  isLifecycleBusy,
}: PlatformPublicationRowProps) {
  // Phase 2B-2 — publication matrice POD (work_variant_id=NULL) : la
  // matrice n'est plus exposée côté FE. On affiche la ligne avec juste
  // l'état + URL Etsy (visibilité du listing live) et on masque le
  // bouton « Mettre à jour » qui n'a pas de chemin matrix-aware en S6.
  // Retirer/Renouveler/Supprimer restent disponibles (ops listing-level).
  const isMatrixPublication = publication.work_variant_id === null
  const variant = isMatrixPublication
    ? null
    : (variants.find((v) => v.id === publication.work_variant_id) ?? null)
  // Matrice POD = toujours physique par construction (groupe N variantes
  // print sur un seul listing Etsy). Pour une variante seule, on lit
  // `medium_kind` ; 'digital' → digital, sinon physique (print / original /
  // limited).
  const isDigital = !isMatrixPublication && variant?.medium_kind === 'digital'
  const mediumLabel = isDigital ? 'Digital' : 'Physical'
  const mediumBadgeVariant = isDigital ? 'info' : 'default'
  const scopeLabel = isMatrixPublication ? 'Matrice' : 'Œuvre'
  const variantLabel = isMatrixPublication ? null : (variant?.label_override ?? null)

  // En S6 le seul lexique d'états câblé est Etsy. Pour Pinterest/Insta
  // S7+, ajouter `PINTEREST_STATE_FR` etc. dans `types/publications.ts`
  // et brancher ici via un dispatcher par platform.
  const stateLabel = ETSY_LISTING_STATE_FR[publication.state] ?? publication.state
  const stateBadgeVariant =
    publication.state === 'active'
      ? 'success'
      : publication.state === 'ended'
        ? 'default'
        : 'warning'
  const mockupsCount = publication.mockups.length

  const canMutate = LIFECYCLE_MUTABLE_STATES.has(publication.state)
  const canRenew = RENEW_ALLOWED_STATES.has(publication.state)
  const canDeleteSmart = !DELETE_SMART_BLOCKED_STATES.has(publication.state)
  const externalUrl = publicationExternalUrl(publication.platform, publication.external_id)

  return (
    <li className="platform-publish-card-item">
      <div className="platform-publish-card-item-meta">
        <Badge
          variant={mediumBadgeVariant}
          size="sm"
          title={
            isDigital
              ? 'Listing Etsy digital (instant download)'
              : 'Listing Etsy physique (print/original)'
          }
        >
          {mediumLabel}
        </Badge>
        <strong>{scopeLabel}</strong>
        {variantLabel ? <span className="ui-body-sm ui-text-muted">{variantLabel}</span> : null}
        <Badge variant={stateBadgeVariant} size="sm" dot={publication.state === 'active'}>
          {stateLabel}
        </Badge>
        {mockupsCount > 0 ? (
          <span className="ui-body-sm ui-text-muted">
            {mockupsCount} mockup{mockupsCount > 1 ? 's' : ''}
          </span>
        ) : null}
      </div>
      <div className="platform-publish-card-item-actions">
        {canMutate ? (
          <>
            {isMatrixPublication ? null : (
              <Button
                type="button"
                variant="quiet"
                size="sm"
                onClick={() => onUpdate(publication)}
                disabled={isLifecycleBusy}
              >
                Mettre à jour
              </Button>
            )}
            {canRenew ? (
              <Button
                type="button"
                variant="quiet"
                size="sm"
                onClick={() => onRenew(publication)}
                disabled={isLifecycleBusy}
              >
                Renouveler
              </Button>
            ) : null}
            <Button
              type="button"
              variant="quiet"
              size="sm"
              onClick={() => onEnd(publication)}
              disabled={isLifecycleBusy}
            >
              Retirer
            </Button>
          </>
        ) : null}
        {canDeleteSmart ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => onDeleteSmart(publication)}
            disabled={isLifecycleBusy}
            title="Probe la plateforme et supprime (les deux côtés si présent, sinon aligne sgraphic.art)"
          >
            <Trash2 aria-hidden="true" size={14} />
            Supprimer
          </Button>
        ) : null}
        {externalUrl ? (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="platform-publish-card-link"
          >
            Voir sur {publication.platform === 'etsy' ? 'Etsy' : publication.platform}{' '}
            <ExternalLink aria-hidden="true" size={14} />
          </a>
        ) : (
          <span className="ui-text-muted">#{publication.external_id}</span>
        )}
      </div>
    </li>
  )
}
